import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border-2 border-[#F2EFEB] h-11 px-3 focus:outline-none focus:border-[#DFFF00] text-sm";

export default function EmailSettings() {
  const [form, setForm] = useState({ from_name: "", reply_to: "" });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [welcome, setWelcome] = useState({ enabled: false, discount_code: "" });
  const [codes, setCodes] = useState([]);
  const [savingWelcome, setSavingWelcome] = useState(false);

  useEffect(() => {
    api.get("/admin/email-settings")
      .then(({ data }) => setForm({ from_name: data.from_name || "", reply_to: data.reply_to || "" }))
      .finally(() => setLoaded(true));
    api.get("/admin/welcome-settings").then(({ data }) => setWelcome(data)).catch(() => {});
    api.get("/admin/discounts").then(({ data }) => setCodes(data.discounts)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-settings", form);
      setForm({ from_name: data.from_name, reply_to: data.reply_to || "" });
      toast.success("Sender details updated — applies to all outgoing emails");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveWelcome = async (e) => {
    e.preventDefault();
    setSavingWelcome(true);
    try {
      const { data } = await api.put("/admin/welcome-settings", welcome);
      setWelcome(data);
      toast.success(data.enabled ? "Welcome email is ON" : "Welcome email is OFF");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingWelcome(false);
    }
  };

  if (!loaded) return <p className="opacity-60 py-8 animate-pulse">Loading settings...</p>;

  return (
    <div className="space-y-8">
    <form onSubmit={save} data-testid="email-settings-form" className="max-w-xl border-2 border-[#F2EFEB]/40 p-6">
      <p className="overline text-[#DFFF00] mb-4">Sender details</p>

      <label className="overline block mb-1">From name</label>
      <input
        className={inputCls}
        value={form.from_name}
        onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
        data-testid="settings-fromname-input"
        placeholder="GOOD GRIEF WORLDWIDE"
        required
      />
      <p className="font-mono-ui text-[0.65rem] opacity-60 mt-1">The display name recipients see. Must be your own brand.</p>

      <label className="overline block mb-1 mt-5">Reply-to address</label>
      <input
        className={inputCls}
        type="email"
        value={form.reply_to}
        onChange={(e) => setForm((f) => ({ ...f, reply_to: e.target.value }))}
        data-testid="settings-replyto-input"
        placeholder="hello@yourdomain.com (optional)"
      />
      <p className="font-mono-ui text-[0.65rem] opacity-60 mt-1">Where customer replies land. Leave blank to use the managed inbox.</p>

      <p className="font-mono-ui text-[0.65rem] opacity-50 mt-4">
        Note: emails send from Emergent's managed address; you control the display name + reply-to here.
        A custom sending domain (e.g. orders@yourdomain.com) requires your own Resend account.
      </p>

      <button type="submit" disabled={saving} data-testid="settings-save-btn" className="mt-6 h-12 px-6 border-2 border-[#F2EFEB] bg-[#F2EFEB] text-[#090909] uppercase tracking-widest text-sm hover:bg-[#DFFF00] hover:border-[#DFFF00] transition-colors disabled:opacity-60">
        {saving ? "Saving..." : "Save sender details →"}
      </button>
    </form>

    <form onSubmit={saveWelcome} data-testid="welcome-settings-form" className="max-w-xl border-2 border-[#F2EFEB]/40 p-6">
      <p className="overline text-[#DFFF00] mb-4">Welcome discount email</p>
      <p className="font-mono-ui text-[0.65rem] opacity-60 mb-4">Auto-emails a discount code to each new person the moment they join any waitlist (once per email address).</p>

      <label className="flex items-center gap-2 text-sm cursor-pointer mb-4">
        <input type="checkbox" checked={welcome.enabled} onChange={(e) => setWelcome((w) => ({ ...w, enabled: e.target.checked }))} data-testid="welcome-enabled-input" />
        Send a welcome discount to new signups
      </label>

      <label className="overline block mb-1">Discount code to send</label>
      <select className={inputCls} value={welcome.discount_code || ""} onChange={(e) => setWelcome((w) => ({ ...w, discount_code: e.target.value }))} data-testid="welcome-code-select">
        <option value="" className="bg-[#090909]">— pick a code —</option>
        {codes.map((c) => (
          <option key={c.id} value={c.code} className="bg-[#090909]">
            {c.code} ({c.type === "percent" ? `${c.amount}% off` : `$${c.amount} off`}){c.is_active ? "" : " — inactive"}
          </option>
        ))}
      </select>
      {codes.length === 0 && <p className="font-mono-ui text-[0.65rem] text-[#FF2A00] mt-1">No discount codes yet — create one in the Discounts tab first.</p>}

      <button type="submit" disabled={savingWelcome} data-testid="welcome-save-btn" className="mt-6 h-12 px-6 border-2 border-[#F2EFEB] bg-[#F2EFEB] text-[#090909] uppercase tracking-widest text-sm hover:bg-[#DFFF00] hover:border-[#DFFF00] transition-colors disabled:opacity-60">
        {savingWelcome ? "Saving..." : "Save welcome email →"}
      </button>
    </form>
    </div>
  );
}
